import {assert} from 'Asserts';
import {isNodeWithInboundSchemaMappers} from 'processes/graph-builder/flowchart-viz/util/isNodeWithInboundSchemaMappers';
import {
  ProcessInteractiveFlowchart,
  ProcessFlowchartInteractiveNode,
} from 'processes/graph-builder-processes/flowchart-viz/ProcessFlowchartVizConstants';
import {
  isOutputNode,
  isRootNode,
  isTaskNode,
} from 'processes/graph-builder-processes/flowchart-viz/util/getNodeDetailsTypeForInteractiveNode';
import getSchemaMapperConfigsMappingToNode from 'processes/graph-builder-processes/util/getSchemaMapperConfigsMappingToNode';
import findFirstMatchingNodeAlongEveryPath from 'util/graph-utils/findFirstMatchingNodeAlongEveryPath';
import {AdjacencyMap} from 'util/graph-utils/GraphUtilConstants';

import {ClientSchemaMapperConfig} from './FlowchartConfigConstants';

export function doesSchemaMapperConfigDependOnSchemas(
  schemaMapperConfig: ClientSchemaMapperConfig,
  targetSchemaIds: Set<string>
): boolean {
  if (schemaMapperConfig.toTaskSchema != null && targetSchemaIds.has(schemaMapperConfig.toTaskSchema.id)) {
    return true;
  }
  return schemaMapperConfig.fromTaskSchemas.find(({id}) => targetSchemaIds.has(id)) != null;
}

export function doesTemplateFlowchartDependOnSchemas(
  templateFlowchart: ProcessInteractiveFlowchart,
  targetSchemaIds: Set<string>
): boolean {
  return templateFlowchart.nodes.some((node) => isTaskNode(node) && targetSchemaIds.has(node.taskSchemaId));
}

export function doesTemplateFlowchartDependOnSchemaMapperConfigs(
  templateFlowchart: ProcessInteractiveFlowchart,
  schemaMapperConfigs: Array<ClientSchemaMapperConfig>,
  targetMapperClientIds: Set<string>
): boolean {
  const fulfilledSchemaMapperConfigs = schemaMapperConfigs
    .filter(({fromTaskSchemas, toTaskSchema}) => fromTaskSchemas.length > 0 && toTaskSchema != null)
    .map(({clientId, fromTaskSchemas, toTaskSchema}) => ({
      clientId,
      fromTaskSchemaIds: fromTaskSchemas.map(({id}) => id),
      toTaskSchemaId: toTaskSchema!.id,
    }));

  const invertedAdjacencyMap: AdjacencyMap = {};
  const taskSchemaIdsByClientId: {[id: string]: string} = {};
  const nodeByClientId: {[id: string]: ProcessFlowchartInteractiveNode} = {};
  for (const node of templateFlowchart.nodes) {
    assert(nodeByClientId[node.clientId] == null, 'Found multiple nodes with the same client id');
    nodeByClientId[node.clientId] = node;
    invertedAdjacencyMap[node.clientId] = [];
    if (isTaskNode(node) || isRootNode(node) || isOutputNode(node)) {
      taskSchemaIdsByClientId[node.clientId] = node.taskSchemaId;
    }
  }
  for (const edge of templateFlowchart.edges) {
    if (invertedAdjacencyMap[edge.toNodeClientId] == null) {
      invertedAdjacencyMap[edge.toNodeClientId] = [];
    }
    invertedAdjacencyMap[edge.toNodeClientId].push(edge.fromNodeClientId);
  }

  for (const node of templateFlowchart.nodes) {
    if (!isNodeWithInboundSchemaMappers(node)) {
      continue;
    }

    const upstreamTaskSchemaNodeIds = findFirstMatchingNodeAlongEveryPath(
      node.clientId,
      invertedAdjacencyMap,
      (nodeId) => isTaskNode(nodeByClientId[nodeId]) || isRootNode(nodeByClientId[nodeId])
    ).map(({nodeId}) => nodeId);

    const inboundTaskSchemaIds = new Set(
      upstreamTaskSchemaNodeIds.map((nodeId) => taskSchemaIdsByClientId[nodeId])
    );
    const mapperConfigsForNode =
      getSchemaMapperConfigsMappingToNode(node, inboundTaskSchemaIds, fulfilledSchemaMapperConfigs) ?? [];

    if (mapperConfigsForNode.some(({clientId}) => targetMapperClientIds.has(clientId))) {
      return true;
    }
  }
  return false;
}
